import { useMutation } from '@apollo/client'
import { useCallback } from 'react'
import {
  DraftConjointItem,
  DraftTextHighlighterItem
} from '../../../data/gql-gen/questionnaire/graphql'
import { DELETE_CUSTOM_AUDIENCE_TRANSLATION } from '../../../data/gql/questionnaire/mutations/deleteCustomAudienceTranslation'
import { DELETE_ENTRY_TRANSLATION } from '../../../data/gql/questionnaire/mutations/deleteEntryTranslation'
import {
  DraftCustomAudience,
  FieldworkAudience,
  FieldworkAudienceType
} from '../../../data/model/audience'
import {
  DraftMatrixItem,
  DraftQuestionItem,
  DraftQuestionnaireEntry,
  DraftTextCardItem,
  EntryType
} from '../../../data/model/questionnaire'
import { useOptionalWaveId } from '../../../hooks/useOptionalWaveId'
import { useProjectId } from '../../../hooks/useProjectId'
import { useSurveyId } from '../../../hooks/useSurveyId'

// Deletes every stored translation for `languageCode` across the supplied
// questionnaire entries and custom audience members. Used by the sidebar's
// "remove language" action after user confirmation. Best-effort: individual
// failures are swallowed so a partial cleanup still makes progress.
export const useDeleteLanguageTranslations = () => {
  const projectId = useProjectId()
  const surveyId = useSurveyId()
  const waveId = useOptionalWaveId()

  const [deleteEntryTranslation] = useMutation(DELETE_ENTRY_TRANSLATION, {
    context: { clientName: 'questionnaire' }
  })
  const [deleteAudienceTranslation] = useMutation(
    DELETE_CUSTOM_AUDIENCE_TRANSLATION,
    { context: { clientName: 'questionnaire' } }
  )

  return useCallback(
    async (
      entries: ReadonlyArray<DraftQuestionnaireEntry>,
      audienceMembers: ReadonlyArray<FieldworkAudience>,
      languageCode: string
    ) => {
      const runEntry = async (
        entryId: string,
        responseOptionId?: string,
        matrixRowQuestionId?: string
      ) => {
        try {
          await deleteEntryTranslation({
            variables: {
              input: {
                projectId,
                surveyId,
                waveId,
                entryId,
                languageCode,
                ...(responseOptionId ? { responseOptionId } : {}),
                ...(matrixRowQuestionId ? { matrixRowQuestionId } : {})
              }
            }
          })
        } catch {
          // Best-effort. A missing translation may 404; ignore.
        }
      }

      const runAudience = async (
        memberId: string,
        responseOptionId?: string
      ) => {
        try {
          await deleteAudienceTranslation({
            variables: {
              input: {
                projectId,
                surveyId,
                waveId,
                memberId,
                languageCode,
                ...(responseOptionId ? { responseOptionId } : {})
              }
            }
          })
        } catch {
          // ignore
        }
      }

      for (const entry of entries) {
        switch (entry.entryType) {
          case EntryType.QuestionEntryType: {
            const item = entry.entryItem as DraftQuestionItem
            const entryId = item.questionLk
            await runEntry(entryId)
            for (const opt of item.responseOptions) {
              const optId = opt.responseOption?.responseOptionId
              if (optId) await runEntry(entryId, optId)
            }
            break
          }
          case EntryType.MatrixEntryType: {
            const item = entry.entryItem as DraftMatrixItem
            const entryId = item.matrixTitleLk
            await runEntry(entryId)
            for (const row of item.matrixRows) {
              const rowQuestionId = row.question?.questionId
              if (rowQuestionId)
                await runEntry(entryId, undefined, rowQuestionId)
            }
            for (const opt of item.responseOptions) {
              const optId = opt.responseOption?.responseOptionId
              if (optId) await runEntry(entryId, optId)
            }
            break
          }
          case EntryType.TextCardEntryType: {
            const item = entry.entryItem as DraftTextCardItem
            await runEntry(item.textCardLk)
            break
          }
          case EntryType.TextHighlighterEntryType: {
            const item = entry.entryItem as DraftTextHighlighterItem
            const entryId = item.question?.questionId
            if (!entryId) break
            // Clears the question text and the passage — one shared record.
            await runEntry(entryId)
            for (const category of item.highlighterCategories) {
              await runEntry(entryId, category.responseOption.responseOptionId)
            }
            break
          }
          case EntryType.ConjointEntryType: {
            const item = entry.entryItem as DraftConjointItem
            const entryId = item.question?.questionId
            // One call: the entry-level delete drops the conjoint's text and
            // every attribute / level translation for this language.
            // The backend (`QuestionnaireEntriesDomainV2.scala:588-592`) filters
            // both `translations` and `attributeTranslations` by language, so a
            // single delete-entry-translation call clears conjoint question text,
            // attribute names, and level values in one pass. Scale/MaxDiff labels
            // are similarly cleared by the entry-level call. Highlighter categories
            // need per-category calls because they are stored as response-option
            // translations.
            if (entryId) await runEntry(entryId)
            break
          }
          default:
            break
        }
      }

      for (const member of audienceMembers) {
        if (member.memberType !== FieldworkAudienceType.Custom) continue
        const item = member.memberItem as DraftCustomAudience
        await runAudience(member.id)
        for (const opt of item.criterionOptions) {
          const optId = opt.responseOption.responseOptionId
          await runAudience(member.id, optId)
        }
      }
    },
    [
      projectId,
      surveyId,
      waveId,
      deleteEntryTranslation,
      deleteAudienceTranslation
    ]
  )
}
